import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppConstants from 'general/constants/AppConstants';
import Utils from 'general/utils/Utils';
import KTBSProgressBar from 'general/components/OtherKeenComponents/KTBSProgressBar';
import PropTypes from 'prop-types';
import AgencyHelper from 'features/Agency/agencyHelper';
import AppConfigs from 'general/constants/AppConfigs';
import agencyApi from 'api/agencyApi';
import UIHelper from 'general/helpers/UIHelper';

NewAgenciesListView.propTypes = {
    agencyRole: PropTypes.string,
    agencyId: PropTypes.string,
    agencyList: PropTypes.array,
    total: PropTypes.number,
    filter: PropTypes.object,
    onFilterChanged: PropTypes.func,
};

const defaultColumnIds = ['AGENCY_CODE', 'AGENCY_NAME', 'AGENCY_LEVEL', 'AGENCY_ROLE', 'AGENCY_TYPE', 'PHONE', 'EMAIL', 'TAXCODE/CCCD', 'STATUS'];

function NewAgenciesListView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyRole, reconciliationDetail } = props;
    const app = useSelector((state) => state.app);
    const { user } = useSelector((state) => state.auth);
    const [operatorList, setOperatorList] = useState([]);
    const [agencyList, setAgencyList] = useState([]);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState({
        parentAgencyId: '',
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        from: '',
        to: '',
        role: '',
    });

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'AGENCY_CODE',
                name: UIHelper.buildTableColumn(agencyRole === 'SALE' ? 'Mã đại lý' : 'Mã đối tác'),
                selector: (row) => row?.agencyCode ?? row.agencyId,
                minWidth: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'AGENCY_NAME',
                name: UIHelper.buildTableColumn(agencyRole === 'SALE' ? 'Tên đại lý' : 'Tên đối tác'),
                selector: (row) => (
                    <KTTooltip offset={[0, 0]} text={row?.name}>
                        <span
                            className="cursor-pointer hover-opacity-60"
                            onClick={() => {
                                AgencyHelper.Navigation.navigateToAgencyDetail(null, row);
                            }}
                        >
                            {row?.name}
                        </span>
                    </KTTooltip>
                ),
                minWidth: '230px',
                style: tableStyle.columns.link,
            },
            {
                id: 'AGENCY_LEVEL',
                name: UIHelper.buildTableColumn('Cấp đại lý'),
                selector: (row) => {
                    return (
                        <span className="min-w-100px">
                            <KTLabel
                                text={DealerHelper.getAgencyLevel(row?.deepLevel)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-primary`}
                            />
                        </span>
                    );
                },
                style: tableStyle.columns.normal,
                width: '140px',
                omit: agencyRole !== 'SALE',
            },
            {
                id: 'AGENCY_ROLE',
                name: UIHelper.buildTableColumn('Vai trò'),
                selector: (row) => t(`agencyRoleDropdownTitle_${row?.primaryRole}`) ?? '',
                style: tableStyle.columns.normal,
                width: '160px',
                omit: agencyRole === 'SALE',
            },
            {
                id: 'AGENCY_TYPE',
                name: UIHelper.buildTableColumn(agencyRole === 'SALE' ? 'Loại đại lý' : 'Loại đối tác'),
                selector: (row) => row.type ? t(`accountType_${row.type}`) : '',
                style: tableStyle.columns.normal,
                minWidth: '140px',
            },
            {
                id: 'PHONE',
                name: UIHelper.buildTableColumn('Số điện thoại'),
                selector: (row) => row.phone ?? '',
                style: tableStyle.columns.normal,
                width: '140px',
            },
            {
                id: 'EMAIL',
                name: UIHelper.buildTableColumn('Email'),
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.email ?? ''}>
                            <span>{row?.email ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.normal,
                minWidth: '250px',
            },
            {
                id: 'TAXCODE/CCCD',
                name: UIHelper.buildTableColumn('MST/CCCD'),
                selector: (row) =>
                    row.type === AppConstants.accountType.individual
                        ? row.idCardNumber?.length > 0
                            ? row.idCardNumber
                            : row.taxCode?.length > 0
                                ? row.taxCode
                                : ''
                        : row.taxCode?.length > 0
                            ? row.taxCode
                            : '',
                style: tableStyle.columns.normal,
                width: '150px',
            },
            {
                id: 'REVENUE',
                name: UIHelper.buildTableColumn('Doanh thu trong tháng', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.revenue),
                width: '160px',
                right: true,
                style: tableStyle.columns.normal,
            },
            {
                id: 'KPI',
                name: UIHelper.buildTableColumn('Tiến độ KPI'),
                selector: (row) => (
                    <div className='w-100'>
                        <KTBSProgressBar
                            completed={(row.saleKPI || row.packageKPI) ? Math.round((row.revenue / (row.saleKPI + row.packageKPI)) * 100) : 100}
                            value={row.revenue}
                            maxValue={row.saleKPI + row.packageKPI}
                            bgColor={AppColors.primary}
                            showValue={false}
                            width={120}
                        />
                    </div>
                ),
                style: tableStyle.columns.normal,
                width: '150px',
                omit: agencyRole !== 'SALE',
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái'),
                minWidth: '150px',
                selector: (row) => (
                    <KTLabel
                        text={t(`agencyStatus_${row.status}`)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${DealerHelper.getAgencyStatusColor(row.status)}`}
                    />
                ),
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                width: '100px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                wrap: true,
                cell: (row) => (
                    <div className="d-flex gap-2 align-items-center">
                        <KTTooltip offset={[0, 0]} text={'Xem chi tiết'}>
                            <a className="btn btn-sm btn-clean btn-icon" onClick={() => handleClickedRow(row)}>
                                <i className="fa-regular fa-eye" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }} />
                            </a>
                        </KTTooltip>
                    </div>
                ),
            },
        ];
    }, [t, operatorList, agencyRole]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    useEffect(() => {
        if (reconciliationDetail?.month) {
            const match = reconciliationDetail.month.match(/^(\d{4})-(\d{2})$/);
            if (match) {
                const [, year, month] = match;
                const { firstDay, lastDay } = Utils.getMonthDateRange(year, month);
                setFilter({ ...filter, from: firstDay, to: lastDay });
            }
        }
    }, [reconciliationDetail]);

    useEffect(() => {
        handleGetNewSalesList();
    }, [filter, reconciliationDetail]);

    // ---- methods ----

    const classifyOperators = (operators) => {
        setOperatorList(
            operators?.map((operator) => ({ operatorId: operator.operatorId, name: operator.name }))
        );
    };

    useEffect(() => {
        if (app.appConfig) {
            classifyOperators(app.appConfig.operators ?? []);
        }
    }, [app.appConfig]);

    const handleClickedRow = (row) => {
        AgencyHelper.Navigation.navigateToAgencyDetail(null, row);
    };

    const handleGetNewSalesList = async () => {
        try {
            const params = {
                ...filter,
                role: agencyRole,
            }
            if (reconciliationDetail) {
                params.parentAgencyId = reconciliationDetail?.agency?.agencyId;
            }
            if (params.from && params.to && params?.parentAgencyId) {
                const { total, agencies } = await agencyApi.getAgencyList(params);
                setAgencyList(agencies);
                setTotal(total);
            }
        } catch (error) {
            console.error('Error fetching new-agencies list:', error);
        }
    };

    return (
        <div>
            {agencyRole === 'SALE'
                ? (
                    <div style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
                        Tổng:
                        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                            {" "} {Utils.formatNumber(total ?? 0)} đại lý
                        </span>
                    </div>
                ) : (
                    <div style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
                        Tổng:
                        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                            {" "} {Utils.formatNumber(total ?? 0)} đối tác
                        </span>
                    </div>
                )}
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={agencyList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(agencyList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={total}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={agencyList.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="đại lý"
                    />
                </div>
            )}
        </div>
    );
}

export default NewAgenciesListView;
